import { useMemo } from 'react';

import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Form, FormikProvider } from 'formik';
import closeFill from '@iconify/icons-eva/close-fill';
import roundClearAll from '@iconify/icons-ic/round-clear-all';
import saveIcon from '@iconify/icons-eva/save-outline';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
// material
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Badge
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../common/LocalizationProvider';
import {
  selectDevicesArray,
  selectGroupSelectedId,
  selectGroupsArray,
  selectSession,
  selectUsersArray
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  zIndex: 999,
  right: 0,
  display: 'flex',
  cursor: 'pointer',
  position: 'fixed',
  alignItems: 'center',
  top: theme.spacing(16),
  height: theme.spacing(5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.25),
  boxShadow: theme.customShadows.z20,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderTopLeftRadius: theme.shape.borderRadiusMd,
  borderBottomLeftRadius: theme.shape.borderRadiusMd,
  transition: theme.transitions.create('opacity'),
  '&:hover': { opacity: 0.72 }
}));
// ----------------------------------------------------------------------

BadgeReportFilterSidebar.propTypes = {
  isOpenFilter: PropTypes.bool,
  onResetFilter: PropTypes.func,
  onOpenFilter: PropTypes.func,
  onCloseFilter: PropTypes.func,
  isSubmitting: PropTypes.func,
  formik: PropTypes.object,
  onSaveFilter: PropTypes.func
};

export default function BadgeReportFilterSidebar({
  isOpenFilter,
  onResetFilter,
  onOpenFilter,
  onCloseFilter,
  onSaveFilter,
  formik
}) {
  const t = useTranslation();
  const { getFieldProps, setFieldValue } = formik;
  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const groups = useSelector(selectGroupsArray);

  const devicesData = useSelector(selectDevicesArray);
  const devices = useMemo(
    () =>
      formik.values.group == null
        ? devicesData
        : devicesData.filter((device) =>
            formik.values.group.id === 0
              ? device.groupId === 0 ||
                session.permission.find((item) => item.groupid === device.groupId) === undefined
              : device.groupId === formik.values.group.id
          ),
    [devicesData, formik.values.group, session.permission]
  );

  const usersData = useSelector(selectUsersArray);
  const users = useMemo(
    () =>
      formik.values.group == null
        ? usersData
        : usersData.filter(
            (user) =>
              user.permission &&
              user.permission.find((item) => item.groupid === formik.values.group.id)
          ),
    [formik.values.group, usersData]
  );

  return (
    <>
      <RootStyle>
        <Badge onClick={onOpenFilter}>
          <Icon icon={roundFilterList} width={24} height={24} />
        </Badge>
      </RootStyle>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate>
          <Drawer
            anchor="right"
            open={isOpenFilter}
            onClose={onCloseFilter}
            PaperProps={{
              sx: { width: 280, border: 'none', overflow: 'hidden' }
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1, py: 2 }}
            >
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                {t('filters')}
              </Typography>
              <IconButton onClick={onCloseFilter}>
                <Icon icon={closeFill} width={20} height={20} />
              </IconButton>
            </Stack>

            <Divider />

            <Stack spacing={3} sx={{ p: 3 }}>
              {session.administrator && (
                <Autocomplete
                  fullWidth
                  {...getFieldProps('group')}
                  options={groups}
                  getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
                  renderInput={(params) => (
                    <TextField {...params} label={t('fields_Club')} variant="standard" />
                  )}
                  onChange={(event, value) => setFieldValue('group', value)}
                />
              )}

              <Autocomplete
                fullWidth
                {...getFieldProps('device')}
                options={devices}
                getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
                renderInput={(params) => (
                  <TextField {...params} label={t('fields_Device')} variant="standard" />
                )}
                onChange={(event, value) => setFieldValue('device', value)}
              />
              {(session.administrator || permission.status < 3) &&
                !(groupId !== 0 && session.administrator) && (
                  <Autocomplete
                    fullWidth
                    {...getFieldProps('user')}
                    options={users}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {`${option.prenom} ${option.name}`}
                      </li>
                    )}
                    getOptionLabel={(option) =>
                      option.name === undefined ? '' : `${option.prenom} ${option.name}`
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={t('fields_User')} variant="standard" />
                    )}
                    onChange={(event, value) => setFieldValue('user', value)}
                  />
                )}
            </Stack>
            <Stack spacing={3} sx={{ p: 3 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel>{t('filters_Period')}</InputLabel>
                <Select {...getFieldProps('period')}>
                  <MenuItem value="thisWeek">{t('filters_Period_ThisWeek')}</MenuItem>
                  <MenuItem value="previousWeek">{t('filters_Period_PreviousWeek')}</MenuItem>
                  <MenuItem value="thisMonth">{t('filters_Period_ThisMonth')}</MenuItem>
                  <MenuItem value="previousMonth">{t('filters_Period_PreviousMonth')}</MenuItem>
                  <MenuItem value="last3Month">{t('filters_Period_Last3Month')}</MenuItem>
                  <MenuItem value="last6Month">{t('filters_Period_Last6Month')}</MenuItem>
                  <MenuItem value="thisYear">{t('filters_Period_ThisYear')}</MenuItem>
                  <MenuItem value="custom">{t('filters_Period_Custom')}</MenuItem>
                </Select>
              </FormControl>

              {getFieldProps('period').value === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label={t('filters_Period_Custom_From')}
                    inputFormat="DD/MM/YYYY"
                    value={getFieldProps('from').value}
                    onChange={(e) => setFieldValue('from', e)}
                    renderInput={(params) => <TextField {...params} fullWidth variant="standard" />}
                  />

                  <DatePicker
                    label={t('filters_Period_Custom_To')}
                    inputFormat="DD/MM/YYYY"
                    value={getFieldProps('to').value}
                    onChange={(e) => setFieldValue('to', e)}
                    renderInput={(params) => <TextField {...params} fullWidth variant="standard" />}
                  />
                </LocalizationProvider>
              )}
            </Stack>

            <Box sx={{ p: 3 }}>
              <Button
                fullWidth
                size="large"
                type="submit"
                color="inherit"
                variant="outlined"
                onClick={onSaveFilter}
                startIcon={<Icon icon={saveIcon} />}
              >
                {t('btn_Apply')}
              </Button>
            </Box>
            <Box sx={{ p: 3 }}>
              <Button
                fullWidth
                size="large"
                type="submit"
                color="inherit"
                variant="outlined"
                onClick={onResetFilter}
                startIcon={<Icon icon={roundClearAll} />}
              >
                {t('btn_Clear')}
              </Button>
            </Box>
          </Drawer>
        </Form>
      </FormikProvider>
    </>
  );
}
